import { getWecomAgentRoutes } from "@/api/system/option";

// 权限分类常量（1-菜单，2-按钮，3-Tab）
const POWER_CATEGORY = {
  MENU: 1,
  BUTTON: 2,
  TAB: 3,
};

// 三类权限各自的本地存储Key
const POWER_STORE_KEY = {
  [POWER_CATEGORY.MENU]: "WECOM_AGENT_POWER_MENU",
  [POWER_CATEGORY.BUTTON]: "WECOM_AGENT_POWER_BUTTON",
  [POWER_CATEGORY.TAB]: "WECOM_AGENT_POWER_TAB",
};

// 将响应中的菜单树按分类提取为 code 集合
const collectPowerCodesByCategory = (routeList = [], targetCategory, resultCodeList = []) => {
  if (!Array.isArray(routeList)) {
    return resultCodeList;
  }

  routeList.forEach((routeItem) => {
    if (!routeItem || typeof routeItem !== "object") {
      return;
    }

    // category=1/2/3 分别对应菜单、按钮、Tab
    if (
      routeItem.category === targetCategory &&
      typeof routeItem.code === "string" &&
      routeItem.code
    ) {
      resultCodeList.push(routeItem.code);
    }

    if (Array.isArray(routeItem.children) && routeItem.children.length > 0) {
      collectPowerCodesByCategory(routeItem.children, targetCategory, resultCodeList);
    }
  });

  return resultCodeList;
};

// 统一读取某一分类权限码数组，返回去重后的数组
const readPowerCodeListFromStore = (storageKey) => {
  const storageValue = localStorage.getItem(storageKey);

  if (!storageValue) {
    return [];
  }

  try {
    const parseValue = JSON.parse(storageValue);
    if (!Array.isArray(parseValue)) {
      return [];
    }
    return [...new Set(parseValue.filter(Boolean))];
  } catch (error) {
    console.error("读取权限缓存失败", error);
    return [];
  }
};

// 统一写入某一分类权限码数组
const writePowerCodeListToStore = (storageKey, codeList = []) => {
  const uniqueCodeList = [...new Set(codeList.filter(Boolean))];
  localStorage.setItem(storageKey, JSON.stringify(uniqueCodeList));
  return uniqueCodeList;
};

// 生成批量查询结果：返回 { code: true/false }
const buildPermissionResult = (allCodeList = [], targetCodeList = []) => {
  const permissionSet = new Set(allCodeList);
  const normalizedCodeList = Array.isArray(targetCodeList)
    ? targetCodeList
    : [targetCodeList];

  return normalizedCodeList.reduce((accumulator, currentCode) => {
    if (!currentCode) {
      return accumulator;
    }
    accumulator[currentCode] = permissionSet.has(currentCode);
    return accumulator;
  }, {});
};

/**
 * 获取企微应用助手权限，并按分类写入 localStorage
 * 同时返回三个分类的权限码数组
 */
export const fetchWecomAgentPowers = async (requestData = {}) => {
  const response = await getWecomAgentRoutes(requestData);
  const routeList = response.data.data || [];

  const menuCodeList = collectPowerCodesByCategory(routeList, POWER_CATEGORY.MENU, []);
  const buttonCodeList = collectPowerCodesByCategory(routeList, POWER_CATEGORY.BUTTON, []);
  const tabCodeList = collectPowerCodesByCategory(routeList, POWER_CATEGORY.TAB, []);

  return {
    menu: writePowerCodeListToStore(POWER_STORE_KEY[POWER_CATEGORY.MENU], menuCodeList),
    button: writePowerCodeListToStore(POWER_STORE_KEY[POWER_CATEGORY.BUTTON], buttonCodeList),
    tab: writePowerCodeListToStore(POWER_STORE_KEY[POWER_CATEGORY.TAB], tabCodeList),
  };
};

/**
 * 批量查询菜单权限（只有顶部菜单用）
 * 输入 code 列表（字符串或数组），返回 { code1: true, code2: false }
 */
export const checkMenuPowers = (powerCodeList) => {
  const menuCodeList = readPowerCodeListFromStore(POWER_STORE_KEY[POWER_CATEGORY.MENU]);
  return buildPermissionResult(menuCodeList, powerCodeList);
};

/**
 * 批量查询按钮权限
 * 输入 code 列表（字符串或数组），返回 { code1: true, code2: false }
 */
export const checkButtonPowers = (powerCodeList) => {
  const buttonCodeList = readPowerCodeListFromStore(POWER_STORE_KEY[POWER_CATEGORY.BUTTON]);
  return buildPermissionResult(buttonCodeList, powerCodeList);
};

/**
 * 批量查询Tab二级菜单权限
 * 输入 code 列表（字符串或数组），返回 { code1: true, code2: false }
 */
export const checkTabPowers = (powerCodeList) => {
  const tabCodeList = readPowerCodeListFromStore(POWER_STORE_KEY[POWER_CATEGORY.TAB]);
  return buildPermissionResult(tabCodeList, powerCodeList);
};

/**
 * 清空全部权限缓存（菜单/按钮/Tab）
 */
export const clearWecomAgentPowers = () => {
  localStorage.removeItem(POWER_STORE_KEY[POWER_CATEGORY.MENU]);
  localStorage.removeItem(POWER_STORE_KEY[POWER_CATEGORY.BUTTON]);
  localStorage.removeItem(POWER_STORE_KEY[POWER_CATEGORY.TAB]);
};
